// Mega Green website content data
// Restructured to match the MegaGreenData shape

export const megaGreenData = {
    site: {
        name: "Mega Green",
        description: "Environmental and social assessments, waste management concepts, renewable energy and waste-to-energy pathways — delivered with clear documentation and real-world execution support.",
        establishedYear: 2020,
    },
    theme: {
        brandGreen: "#77A252",
        brandBlue: "#1982BD",
        brandOrange: "#F0A02C",
        brandPurple: "#684190",
        neutralGray: "#A1A2A7",
        neutralDark: "#595A5D",
        bg: "#FFFFFF",
        surface: "#F7F8F9",
        border: "#E3E5E8",
    },
    nav: {
        primary: [
            { label: "Home", path: "/" },
            { label: "About", path: "/about" },
            { label: "Services", path: "/services" },
            { label: "Sectors", path: "/sectors" },
            { label: "Projects", path: "/projects" },
            { label: "Insights", path: "/insights" },
            { label: "Downloads", path: "/downloads" },
            { label: "Contact", path: "/contact" },
        ],
        secondary: [
            { label: "Privacy Policy", path: "/legal/privacy" },
            { label: "Terms of Service", path: "/legal/terms" },
        ],
    },
    home: {
        hero: {
            h1: "Integrated Environmental Expertise, From Study to Approval",
            subhead: "Mega Green delivers integrated environmental consulting for industrial and infrastructure projects—combining assessment, compliance systems, and implementation support to help projects pass review and operate responsibly.",
            primaryCTA: {
                label: "Request a Consultation",
                to: "/contact"
            },
            secondaryCTA: {
                label: "Explore Services",
                to: "/services"
            },
            image: {
                src: "/Home/hero2.jpg",
                alt: "Modern renewable energy blended with a clean industrial facility/control room (hero)"
            }
        },
        pillars: [
            {
                title: "Assessments",
                desc: "EIA, limited EIA, Category (C), social and sustainability assessments.",
                icon: "DocumentSearch",
                imageAlt: "Map/report visual",
                src: "/Home/pillars/assessments.webp"
            },
            {
                title: "Waste systems",
                desc: "Waste audits, concepts, hazardous stream routing, traceability workflows.",
                icon: "Recycle",
                imageAlt: "Sorting and storage visual",
                src: "/Home/pillars/waste-systems.webp"
            },
            {
                title: "Energy pathways",
                desc: "Bioenergy and waste-to-energy feasibility and concept design.",
                icon: "Bolt",
                imageAlt: "Renewable energy visual",
                src: "/Home/pillars/energy-pathways.webp"
            }
        ],
        proofStrip: [
            { label: "Established 2020.", icon: "calendar" },
            { label: "Multi-sector portfolio across industry, infrastructure, education, and agriculture.", icon: "shield" },
            { label: "Compliance-first deliverables: authority-ready studies and audit-ready registers.", icon: "document" }
        ],
        featuredProjects: [
            "EIA-ROB-01",
            "EIA-WAT-001",
            "EIA-WTE-001",
            "EIA-INF-001",
            "EIA-ROB-02",
            "EIA-CATC-001",
            "EIA-ROB-03",
            "EIA-ROB-04"
        ],
        featuredInsights: [
            "environmental-impact-assessment-eia-what-it-is-and-why-it-accelerates-approvals",
            "limited-eia-vs-full-eia-choosing-the-proportional-path",
            "road-project-eias-managing-dust-noise-and-community-safety"
        ],
        homeContactForm: {
            fields: [
                "name",
                "company",
                "email",
                "phone",
                "serviceNeeded",
                "message"
            ],
            serviceNeededOptions: [
                "Environmental and Social Assessments",
                "Environmental Compliance and Registers",
                "Waste Management and Hazardous Streams",
                "Renewable Energy and Waste-to-Energy",
                "Training and Certifications"
            ]
        },
    },
    about: {
        intro: "Established in 2020, Mega Green delivers environmental and sustainability work that is designed to pass review, support implementation, and remain audit-ready.",
        whoWeAre: "Mega Green is an environmental consultancy established in 2020. We support projects that must be approved, built, and operated responsibly — with studies and systems that are clear, defensible, and implementable.",
        mission: "Help project owners reduce environmental risk and accelerate approvals by delivering clear studies, practical mitigation, and measurable monitoring programs.",
        src: "/About/about.jpg",
        values: [
            {
                title: "Technical rigor",
                desc: "Decisions supported by data and clear assumptions."
            },
            {
                title: "Practicality",
                desc: "Mitigation that can be implemented on real sites."
            },
            {
                title: "Compliance-first",
                desc: "Documentation aligned with authority expectations."
            },
            {
                title: "Transparency",
                desc: "Clear scope, clear outputs, and clear limitations."
            },
            {
                title: "Safety",
                desc: "Hazardous handling and incident prevention built into plans."
            }
        ],
        howWeWorkSteps: [
            {
                title: "Define the approval target",
                desc: "Clarify authority pathway, scope, and submission expectations."
            },
            {
                title: "Build the baseline",
                desc: "Site understanding, data plan, and risk map before writing."
            },
            {
                title: "Design mitigation you can implement",
                desc: "Controls, responsibilities, and budgets that match real operations."
            },
            {
                title: "Deliver audit-ready documentation",
                desc: "Clear structure, registers, and monitoring templates for implementation."
            }
        ]
    },
    services: {
        overviewIntro: "Mega Green delivers five integrated service lines. Each service can be scoped as a stand-alone deliverable or bundled as an approval-to-operations package.",
        services: [
            {
                id: "assessments",
                title: "Environmental and Social Assessments",
                short: "EIA, limited EIA, Category (C), and sustainability assessments designed to pass review and support implementation.",
                ctaLabel: "Explore Assessments",
                route: "/services/assessments",
                icon: "DocumentSearch",
                bullets: [
                    "EIA / ESIA documentation aligned with authority expectations",
                    "Baseline data plans, impact assessment, mitigation and EMP",
                    "Monitoring programs with measurable KPIs"
                ]
            },
            {
                id: "compliance-registers",
                title: "Environmental Compliance and Registers",
                short: "Operational compliance systems: legal registers, hazardous materials and waste registers, and audit-ready documentation.",
                ctaLabel: "Explore Compliance",
                route: "/services/compliance-registers",
                icon: "ShieldCheck",
                bullets: [
                    "Environmental legal register and obligations tracking",
                    "Hazardous materials & waste registers (SDS, storage, segregation)",
                    "Inspection readiness checklists and reporting templates"
                ]
            },
            {
                id: "waste-management",
                title: "Waste Management and Hazardous Streams",
                short: "Waste audits, concepts, routing, storage, and traceability workflows that work on real sites.",
                ctaLabel: "Explore Waste Systems",
                route: "/services/waste-management",
                icon: "Recycle",
                bullets: [
                    "Waste audits and material flow mapping",
                    "Segregation, storage, and contractor SOPs",
                    "Traceability workflows and chain-of-custody"
                ]
            },
            {
                id: "waste-to-energy",
                title: "Renewable Energy and Waste-to-Energy",
                short: "Bioenergy and WtE feasibility and concept design — with conservative assumptions and clear decision gates.",
                ctaLabel: "Explore WtE",
                route: "/services/waste-to-energy",
                icon: "Bolt",
                bullets: [
                    "Feedstock and supply-risk assessment",
                    "Technology screening and mass/energy balance",
                    "Concept design, permitting pathway, and CAPEX/OPEX framing"
                ]
            },
            {
                id: "training",
                title: "Training and Certifications",
                short: "Capacity building for EHS teams, contractors, and operators — focused on procedures, documentation, and audits.",
                ctaLabel: "Explore Training",
                route: "/services/training",
                icon: "GraduationCap",
                bullets: [
                    "Role-based training plans and competency checks",
                    "Operational SOP coaching and documentation workshops",
                    "Audit readiness and inspection simulation"
                ]
            }
        ],
        detailPages: {
            assessments: {
                hero: {
                    title: "Environmental and Social Assessments",
                    subtitle: "EIA, limited EIA, and Category (C) studies structured for authority review — and written for real implementation.",
                    imageAlt: "Environmental report pages, site survey, and field sampling (service hero)"
                },
                whatWeDeliver: [
                    {
                        title: "EIA / ESIA packages",
                        desc: "Structured submission-ready documentation aligned with authority expectations."
                    },
                    {
                        title: "Limited EIA for modifications",
                        desc: "Proportional assessments for upgrades, additions, or changes in process/throughput."
                    },
                    {
                        title: "Category (C) EIAs",
                        desc: "Large-scope studies for nationwide operations, multi-governorate programs, and broad waste classes."
                    },
                    {
                        title: "EMP & monitoring programs",
                        desc: "Mitigation actions, responsibilities, sampling plans, and KPIs that can be audited."
                    },
                    {
                        title: "Implementation-ready checklists",
                        desc: "Simple controls and templates your team can actually use on site."
                    }
                ],
                whatWeNeedFromYou: [
                    "Project description and objectives (what’s changing, what’s new).",
                    "Site location, layout, and relevant drawings (or a site walk-through).",
                    "Process flow and utilities (inputs/outputs, energy, water).",
                    "Waste and wastewater information (types, estimated quantities, existing handling).",
                    "Any existing permits, previous studies, or authority correspondence."
                ],
                typicalTimeline: [
                    {
                        phase: "Kickoff & scoping",
                        duration: "2–5 days",
                        notes: "Define approval target, data plan, and submission structure."
                    },
                    {
                        phase: "Baseline & data collection",
                        duration: "1–3 weeks",
                        notes: "Site understanding, surveys, and required supporting documents."
                    },
                    {
                        phase: "Assessment & drafting",
                        duration: "1–3 weeks",
                        notes: "Impact assessment, mitigation, EMP, monitoring program."
                    },
                    {
                        phase: "Finalization & submission support",
                        duration: "3–7 days",
                        notes: "Quality checks, formatting, and reviewer-ready annexes."
                    }
                ],
                faqs: [
                    {
                        q: "Do you handle limited EIA for expansions and upgrades?",
                        a: "Yes — we scope proportional studies that focus on deltas (new equipment, new wastes, new emissions) while keeping the submission defensible."
                    },
                    {
                        q: "Can you support authority review questions?",
                        a: "Yes — we provide response packs and clarifications aligned with the submitted study."
                    },
                    {
                        q: "What if we don’t have complete baseline data?",
                        a: "We build a practical data plan and define assumptions clearly, then close gaps with targeted sampling or conservative design decisions."
                    }
                ]
            },
            "compliance-registers": {
                hero: {
                    title: "Environmental Compliance and Registers",
                    subtitle: "Audit-ready registers, legal obligations tracking, and inspection preparation that reduces operational risk.",
                    imageAlt: "Compliance checklist, SDS binder, and labeled storage areas (service hero)"
                },
                whatWeDeliver: [
                    {
                        title: "Environmental legal register",
                        desc: "Applicable laws/permits mapped to obligations and evidence required."
                    },
                    {
                        title: "Hazardous materials register",
                        desc: "Inventory with SDS references, storage/segregation rules, and handling controls."
                    },
                    {
                        title: "Hazardous waste register",
                        desc: "Waste classification, storage limits, manifests, and contractor documentation workflow."
                    },
                    {
                        title: "Gap assessment & action plan",
                        desc: "What’s missing, what to fix first, and how to document it for inspection."
                    },
                    {
                        title: "Inspection simulation checklist",
                        desc: "Site walkthrough checklist and documentation package checklist."
                    }
                ],
                whatWeNeedFromYou: [
                    "Current permits and approvals (if available).",
                    "Chemical inventory and SDS files (or access to procurement lists).",
                    "Waste contractors’ details and existing manifests/records.",
                    "Site storage layouts and handling SOPs (if any).",
                    "Recent inspection notes or internal audit findings (if any)."
                ],
                typicalTimeline: [
                    {
                        phase: "Data collection",
                        duration: "3–7 days",
                        notes: "Gather documents, inventory, and site walkthrough."
                    },
                    {
                        phase: "Register drafting",
                        duration: "1–2 weeks",
                        notes: "Build registers + evidence structure and responsibilities."
                    },
                    {
                        phase: "Gap closure workshop",
                        duration: "2–5 days",
                        notes: "Action plan, quick wins, and inspection simulation."
                    }
                ],
                faqs: [
                    {
                        q: "Are registers just paperwork?",
                        a: "No — they’re operational control tools. We build them so they match how your site actually works."
                    },
                    {
                        q: "Can you integrate registers with existing EHS systems?",
                        a: "Yes — we can align formats to your current templates and reporting rhythm."
                    }
                ]
            },
            "waste-management": {
                hero: {
                    title: "Waste Management and Hazardous Streams",
                    subtitle: "Waste audits, practical concepts, storage/segregation design, and traceability workflows that hold up in the real world.",
                    imageAlt: "Waste segregation stations and storage area with clear labels (service hero)"
                },
                whatWeDeliver: [
                    {
                        title: "Waste audit & material flow",
                        desc: "Quantities, types, sources, and current handling mapped into a usable baseline."
                    },
                    {
                        title: "Waste management concept",
                        desc: "Segregation, storage, routing, contractor roles, SOPs, and KPIs."
                    },
                    {
                        title: "Hazardous stream controls",
                        desc: "Storage/segregation rules, labeling, spill controls, and manifests."
                    },
                    {
                        title: "Contractor & chain-of-custody workflow",
                        desc: "Documentation templates and responsibilities for traceability."
                    },
                    {
                        title: "Site rollout support",
                        desc: "Workshops, signage guidance, and implementation checklists."
                    }
                ],
                whatWeNeedFromYou: [
                    "Waste types and approximate quantities (even rough estimates are fine).",
                    "Current contractor details and pickup schedules.",
                    "Site layout and storage constraints.",
                    "Any existing SOPs, labels, or internal waste logs.",
                    "Target compliance goal (inspection readiness / certification / client requirements)."
                ],
                typicalTimeline: [
                    {
                        phase: "Audit & baseline mapping",
                        duration: "1–2 weeks",
                        notes: "Site walkthrough + data capture."
                    },
                    {
                        phase: "Concept & SOP drafting",
                        duration: "1–2 weeks",
                        notes: "Routing, storage, responsibilities, KPIs."
                    },
                    {
                        phase: "Rollout & training",
                        duration: "3–10 days",
                        notes: "Briefings, signage, and launch checklist."
                    }
                ],
                faqs: [
                    {
                        q: "Can you cover nationwide operations (many governorates)?",
                        a: "Yes — we’ve supported large-scope waste operators and can design documentation that scales across locations."
                    }
                ]
            },
            "waste-to-energy": {
                hero: {
                    title: "Renewable Energy, Bioenergy, and Waste-to-Energy",
                    subtitle: "Feasibility and concept design with conservative assumptions, clear decision gates, and permitting pathways.",
                    imageAlt: "Renewable energy facility concept render overlaid with charts (service hero)"
                },
                whatWeDeliver: [
                    {
                        title: "Feedstock assessment",
                        desc: "Availability, seasonality, logistics, contamination risk, and supply contracts assumptions."
                    },
                    {
                        title: "Technology screening",
                        desc: "Practical screening of pathways (RDF, biodiesel, biogas, thermal processes) with pros/cons."
                    },
                    {
                        title: "Mass & energy balance",
                        desc: "Transparent calculations and sensitivity cases."
                    },
                    {
                        title: "Concept design & layout",
                        desc: "High-level block flow, footprint, and interfaces with utilities."
                    },
                    {
                        title: "Permitting roadmap",
                        desc: "EIA pathway, stakeholder map, and decision milestones."
                    }
                ],
                whatWeNeedFromYou: [
                    "Target output and concept (power/heat/fuel; scale range).",
                    "Feedstock assumptions (type, volumes, sourcing radius).",
                    "Site options, land constraints, utilities availability.",
                    "Offtake concept (who buys the output, pricing assumptions if any).",
                    "Any pre-existing studies or vendor proposals."
                ],
                typicalTimeline: [
                    {
                        phase: "Feasibility framing",
                        duration: "3–7 days",
                        notes: "Define pathways, data needs, and gating assumptions."
                    },
                    {
                        phase: "Assessment & modeling",
                        duration: "2–5 weeks",
                        notes: "Feedstock, technology, balances, CAPEX/OPEX framing."
                    },
                    {
                        phase: "Concept package",
                        duration: "1–2 weeks",
                        notes: "Concept layout + permitting roadmap."
                    }
                ],
                faqs: [
                    {
                        q: "Do you recommend a single technology?",
                        a: "We screen options first, then recommend based on feedstock risk, permitting constraints, and economics — with assumptions stated clearly."
                    }
                ]
            },
            training: {
                hero: {
                    title: "Training, Capacity Building, and Certifications",
                    subtitle: "Role-based training that strengthens SOPs, documentation, and inspection readiness — not generic lectures.",
                    imageAlt: "Training workshop with procedures checklist and site signage (service hero)"
                },
                whatWeDeliver: [
                    {
                        title: "Needs assessment",
                        desc: "Role map and skill gaps tied to site procedures and inspections."
                    },
                    {
                        title: "Training modules",
                        desc: "Tailored modules (waste handling, hazardous storage, documentation, EIA/EMP rollout)."
                    },
                    {
                        title: "Competency checks",
                        desc: "Quizzes and practical checks to confirm learning."
                    },
                    {
                        title: "On-site coaching",
                        desc: "Walkthroughs and procedure refinement with operators and supervisors."
                    },
                    {
                        title: "Certification roadmap support",
                        desc: "Guidance on what evidence and routines are needed for common certification pathways."
                    }
                ],
                whatWeNeedFromYou: [
                    "Target audience roles and headcount.",
                    "Existing SOPs and any recurring incidents or pain points.",
                    "Inspection/certification target (if applicable).",
                    "Shift schedule constraints for training planning."
                ],
                typicalTimeline: [
                    {
                        phase: "Design",
                        duration: "3–7 days",
                        notes: "Modules, agenda, and materials."
                    },
                    {
                        phase: "Delivery",
                        duration: "1–3 days (per cohort)",
                        notes: "Workshops + hands-on walkthroughs."
                    },
                    {
                        phase: "Follow-up",
                        duration: "1–2 weeks",
                        notes: "Competency checks, improvements, and evidence pack."
                    }
                ],
                faqs: [
                    {
                        q: "Can you train contractors too?",
                        a: "Yes — contractor-facing sessions are often the fastest way to reduce on-site compliance failures."
                    }
                ]
            }
        }
    },
    sectors: {
        intro: "Sector-specific experience, delivered with consistent documentation and realistic mitigation planning.",
        sectors: [
            {
                id: "leather",
                title: "Leather and Tanneries",
                short: "Complex wastewater, sludge management, and odor controls — with strict discharge requirements.",
                commonChallenges: [
                    "High-strength wastewater and variable loads",
                    "Sludge handling, reuse/disposal, and documentation",
                    "Odor and community sensitivity"
                ],
                howWeHelp: [
                    "EIAs and upgrade documentation (limited EIA where applicable)",
                    "WWTP integration, sludge routing, and monitoring plans",
                    "Audit-ready registers for chemicals and hazardous streams"
                ],
                route: "/sectors#leather",
                src: "/Sectors/leather.jpg"
            },
            {
                id: "industrial",
                title: "Industrial and Manufacturing",
                short: "Approvals and operational compliance for plants, expansions, and utilities.",
                commonChallenges: [
                    "Permitting and authority review cycles",
                    "Industrial waste, wastewater, and emissions compliance",
                    "Operational documentation and audit readiness"
                ],
                howWeHelp: [
                    "EIA/limited EIA with implementable mitigation",
                    "Waste and wastewater management concepts",
                    "Registers and training for inspections"
                ],
                route: "/sectors#industrial",
                src: "/Sectors/industrial.jpg"
            },
            {
                id: "waste",
                title: "Waste Operators and Nationwide Collection",
                short: "Nationwide scope approvals, traceability, and chain-of-custody workflows.",
                commonChallenges: [
                    "Approvals across wide geographic scope and waste classes",
                    "Manifesting, traceability, and contractor chain-of-custody",
                    "Interim storage and transfer infrastructure"
                ],
                howWeHelp: [
                    "Category (C) EIAs for national-scale operations",
                    "Documentation workflows and training",
                    "KPI definition for recovery, incident rates, and audit performance"
                ],
                route: "/sectors#waste",
                src: "/Sectors/waste.jpg"
            },
            {
                id: "water",
                title: "Water and Wastewater",
                short: "Treatment upgrades, compliance documentation, and monitoring programs.",
                commonChallenges: [
                    "Process changes affecting effluent quality",
                    "High salinity / special discharge constraints",
                    "Reuse targets and water balance planning"
                ],
                howWeHelp: [
                    "EIA/limited EIA for treatment additions and modifications",
                    "Monitoring plans, sampling schedules, and KPIs",
                    "Discharge documentation and mitigation pathways"
                ],
                route: "/sectors#water",
                src: "/Sectors/water.jpg"
            },
            {
                id: "infrastructure",
                title: "Infrastructure and Roads",
                short: "Construction impacts, traffic, and community safety along corridors.",
                commonChallenges: [
                    "Dust, noise, detours, and community safety",
                    "Sensitive receptors along routes",
                    "Material sourcing and spoil management"
                ],
                howWeHelp: [
                    "EIA separating construction vs operation impacts",
                    "Mitigation plans with enforceable contractor requirements",
                    "Monitoring checklists and reporting templates"
                ],
                route: "/sectors#infrastructure",
                src: "/Sectors/infrastructure.jpg"
            },
            {
                id: "education",
                title: "Education Campuses",
                short: "Construction controls plus operational waste/wastewater planning for growing populations.",
                commonChallenges: [
                    "Traffic and pedestrian safety",
                    "Noise during construction and utilities loads",
                    "Stakeholder communication and complaints"
                ],
                howWeHelp: [
                    "EIA addressing construction controls and operational management plans",
                    "Waste segregation and facility SOPs",
                    "Engagement messaging and complaint response workflows"
                ],
                route: "/sectors#education",
                src: "/Sectors/education.jpg"
            },
            {
                id: "agriculture",
                title: "Agriculture Investment and Land Reclamation",
                short: "Large-footprint programs with water/soil focus and monitoring KPIs.",
                commonChallenges: [
                    "Water balance, soil quality, and ecosystem protection",
                    "Irrigation management and runoff controls",
                    "Authority coordination for large programs"
                ],
                howWeHelp: [
                    "Category (C) EIAs with water and soil focus",
                    "Mitigation for erosion, salinization risk, habitat impacts",
                    "Monitoring KPIs and reporting schedule templates"
                ],
                route: "/sectors#agriculture",
                src: "/Sectors/agriculture.jpg"
            },
            {
                id: "renewable",
                title: "Renewable Fuels and Bioenergy",
                short: "Feasibility and EIA support for biodiesel, ethanol expansion, and related pathways.",
                commonChallenges: [
                    "Feedstock variability and supply risk",
                    "Permitting and compliance documentation",
                    "Operational safety and hazardous handling"
                ],
                howWeHelp: [
                    "EIA/limited EIA for renewables expansions",
                    "Feasibility and concept design for bioenergy/WtE",
                    "Registers and training for hazardous streams"
                ],
                route: "/sectors#renewable",
                src: "/Sectors/renewable.jpg"
            }
        ]
    },
    projects: {
        intro: "A structured library of EIAs and compliance work delivered across Egypt. Use filters to find relevant experience quickly.",
        projects: [
            {
                id: "EIA-ROB-01",
                slug: "eia-rob-01-mohamed-harby-and-partners",
                client: "Mohamed Harby and Partners",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for Mohamed Harby and Partners (EIA-ROB-01)"
                }
            },
            {
                id: "EIA-ROB-02",
                slug: "eia-rob-02-tannery-geneh-mostafa-hussein-abdel-khaleq",
                client: "Tannery \"Geneh\" (Mostafa Hussein Abdel Khaleq)",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for Tannery \"Geneh\" (Mostafa Hussein Abdel Khaleq) (EIA-ROB-02)"
                }
            },
            {
                id: "EIA-ROB-03",
                slug: "eia-rob-03-mahmoud-mohamed-sarag-tannery",
                client: "Mahmoud Mohamed Sarag Tannery",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for Mahmoud Mohamed Sarag Tannery (EIA-ROB-03)"
                }
            },
            {
                id: "EIA-ROB-04",
                slug: "eia-rob-04-el-montazah-tannery-hamdy-mohamed-harb",
                client: "El-Montazah Tannery (Hamdy Mohamed Harb)",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for El-Montazah Tannery (Hamdy Mohamed Harb) (EIA-ROB-04)"
                }
            },
            {
                id: "EIA-ROB-05",
                slug: "eia-rob-05-german-industry-and-trade-company-el-gebbas",
                client: "German Industry and Trade Company \"El-Gebbas\"",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for German Industry and Trade Company \"El-Gebbas\" (EIA-ROB-05)"
                }
            },
            {
                id: "EIA-ROB-06",
                slug: "eia-rob-06-ahmed-harby-and-partners",
                client: "Ahmed Harby and Partners",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for Ahmed Harby and Partners (EIA-ROB-06)"
                }
            },
            {
                id: "EIA-ROB-07",
                slug: "eia-rob-07-ammar-bilal-ammar-tannery",
                client: "Ammar Bilal Ammar Tannery",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for Ammar Bilal Ammar Tannery (EIA-ROB-07)"
                }
            },
            {
                id: "EIA-ROB-08",
                slug: "eia-rob-08-serag-ammar-tannery",
                client: "Serag Ammar Tannery",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Leather / Tanneries project thumbnail for Serag Ammar Tannery (EIA-ROB-08)"
                }
            },
            {
                id: "EIA-IND-001",
                slug: "eia-ind-001-national-paints-factories-company",
                client: "National Paints Factories Company",
                projectType: "EIA",
                sector: "Paints / Chemicals",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Paints / Chemicals project thumbnail for National Paints Factories Company (EIA-IND-001)"
                }
            },
            {
                id: "EIA-IND-002",
                slug: "eia-ind-002-smart-home-for-electrical-industries",
                client: "Smart Home for Electrical Industries",
                projectType: "EIA",
                sector: "Electrical Manufacturing",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Electrical Manufacturing project thumbnail for Smart Home for Electrical Industries (EIA-IND-002)"
                }
            },
            {
                id: "EIA-IND-003",
                slug: "eia-ind-003-aura-aerosols",
                client: "Aura Aerosols",
                projectType: "EIA",
                sector: "Aerosols / FMCG",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Aerosols / FMCG project thumbnail for Aura Aerosols (EIA-IND-003)"
                }
            },
            {
                id: "EIA-IND-004",
                slug: "eia-ind-004-egyptian-international-company-for-textile-and-garment-trading-manufacturing",
                client: "Egyptian International Company for Textile and Garment Trading & Manufacturing",
                projectType: "EIA",
                sector: "Textiles / Garments",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Textiles / Garments project thumbnail for Egyptian International Company for Textile and Garment Trading & Manufacturing (EIA-IND-004)"
                }
            },
            {
                id: "EIA-IND-005",
                slug: "eia-ind-005-italian-company-for-extracting-and-refining-oils",
                client: "Italian Company for Extracting and Refining Oils",
                projectType: "EIA",
                sector: "Oils / Food Industry",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Oils / Food Industry project thumbnail for Italian Company for Extracting and Refining Oils (EIA-IND-005)"
                }
            },
            {
                id: "EIA-WAT-001",
                slug: "eia-wat-001-egyptian-propylene-and-polypropylene-production-company",
                client: "Egyptian Propylene and Polypropylene Production Company",
                projectType: "EIA",
                sector: "Petrochemical / Water",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The project team gained a defensible plan for managing salinity-related risks and proving compliance\nover time.",
                thumbnail: {
                    src: "",
                    alt: "Petrochemical / Water project thumbnail for Egyptian Propylene and Polypropylene Production Company (EIA-WAT-001)"
                }
            },
            {
                id: "EIA-IND-006",
                slug: "eia-ind-006-raad-glass-factory",
                client: "Raad Glass Factory",
                projectType: "EIA",
                sector: "Glass Manufacturing",
                location: "Sadat City, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Glass Manufacturing project thumbnail for Raad Glass Factory (EIA-IND-006)"
                }
            },
            {
                id: "EIA-IND-007",
                slug: "eia-ind-007-arab-company-for-resins-and-chemical-industries",
                client: "Arab Company for Resins and Chemical Industries",
                projectType: "EIA",
                sector: "Resins / Chemicals",
                location: "New Borg El-Arab, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Resins / Chemicals project thumbnail for Arab Company for Resins and Chemical Industries (EIA-IND-007)"
                }
            },
            {
                id: "EIA-IND-008",
                slug: "eia-ind-008-e-star-egypt-for-industry-s-a-e",
                client: "E-Star Egypt for Industry (S.A.E.)",
                projectType: "EIA",
                sector: "Steel / Industry",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Steel / Industry project thumbnail for E-Star Egypt for Industry (S.A.E.) (EIA-IND-008)"
                }
            },
            {
                id: "EIA-IND-009",
                slug: "eia-ind-009-al-andalus-iron-industries",
                client: "Al-Andalus Iron Industries",
                projectType: "EIA",
                sector: "Metals / Foundry",
                location: "Beni Suef, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Metals / Foundry project thumbnail for Al-Andalus Iron Industries (EIA-IND-009)"
                }
            },
            {
                id: "EIA-LIM-001",
                slug: "eia-lim-001-egyptian-propylene-and-polypropylene-production-company",
                client: "Egyptian Propylene and Polypropylene Production Company",
                projectType: "Limited EIA",
                sector: "Petrochemical",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Petrochemical project thumbnail for Egyptian Propylene and Polypropylene Production Company (EIA-LIM-001)"
                }
            },
            {
                id: "EIA-LIM-002",
                slug: "eia-lim-002-group-sp-egypt-for-home-appliances",
                client: "Group SP Egypt for Home Appliances",
                projectType: "Limited EIA",
                sector: "Home Appliances",
                location: "Borg El-Arab, Alexandria, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Home Appliances project thumbnail for Group SP Egypt for Home Appliances (EIA-LIM-002)"
                }
            },
            {
                id: "EIA-LIM-003",
                slug: "eia-lim-003-al-anwar-for-extraction-of-vegetable-oils",
                client: "Al-Anwar for Extraction of Vegetable Oils",
                projectType: "Limited EIA",
                sector: "Oils / Food Industry",
                location: "Beheira, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Oils / Food Industry project thumbnail for Al-Anwar for Extraction of Vegetable Oils (EIA-LIM-003)"
                }
            },
            {
                id: "EIA-INF-001",
                slug: "eia-inf-001-road-project-abu-omar-road",
                client: "Road Project (Abu Omar Road)",
                projectType: "EIA",
                sector: "Infrastructure / Roads",
                location: "Kafr El-Sheikh Governorate, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study supported a clearer approvals pathway and translated mitigation into site-enforceable\nactions.",
                thumbnail: {
                    src: "",
                    alt: "Infrastructure / Roads project thumbnail for Road Project (Abu Omar Road) (EIA-INF-001)"
                }
            },
            {
                id: "EIA-IND-010",
                slug: "eia-ind-010-modern-chemical-industries-mci",
                client: "Modern Chemical Industries (MCI)",
                projectType: "EIA",
                sector: "Chemicals",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Chemicals project thumbnail for Modern Chemical Industries (MCI) (EIA-IND-010)"
                }
            },
            {
                id: "EIA-IND-011",
                slug: "eia-ind-011-egyptian-international-textiles-company",
                client: "Egyptian International Textiles Company",
                projectType: "EIA",
                sector: "Textiles",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Textiles project thumbnail for Egyptian International Textiles Company (EIA-IND-011)"
                }
            },
            {
                id: "EIA-WTE-001",
                slug: "eia-wte-001-tagaddod-for-software-and-new-renewable-energy-services",
                client: "Tagaddod for Software and New & Renewable Energy Services",
                projectType: "EIA",
                sector: "Renewable Fuels",
                location: "Egypt",
                primaryServiceLine: "Renewable Energy, Bioenergy, and Waste-to-Energy",
                oneSentenceOutcome: "The project gained a clearer compliance basis for renewable fuels operations and expansion planning.",
                thumbnail: {
                    src: "",
                    alt: "Renewable Fuels project thumbnail for Tagaddod for Software and New & Renewable Energy Services (EIA-WTE-001)"
                }
            },
            {
                id: "EIA-INF-002",
                slug: "eia-inf-002-road-project-kafr-askar-link",
                client: "Road Project (Kafr Askar link)",
                projectType: "EIA",
                sector: "Infrastructure / Roads",
                location: "Kafr El-Sheikh Governorate, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study supported a clearer approvals pathway and translated mitigation into site-enforceable\nactions.",
                thumbnail: {
                    src: "",
                    alt: "Infrastructure / Roads project thumbnail for Road Project (Kafr Askar link) (EIA-INF-002)"
                }
            },
            {
                id: "EIA-CATC-001",
                slug: "eia-catc-001-egyptian-environmental-services-company-expansion",
                client: "Egyptian Environmental Services Company (expansion)",
                projectType: "Category (C) EIA",
                sector: "Waste Services / Nationwide",
                location: "Egypt (nationwide)",
                primaryServiceLine: "Waste Management and Hazardous Streams",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Waste Services / Nationwide project thumbnail for Egyptian Environmental Services Company (expansion) (EIA-CATC-001)"
                }
            },
            {
                id: "EIA-IND-012",
                slug: "eia-ind-012-global-pharmaceutical-industries",
                client: "Global Pharmaceutical Industries",
                projectType: "EIA",
                sector: "Pharmaceuticals",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The expansion assessment supported review readiness and controlled transition into upgraded\noperations.",
                thumbnail: {
                    src: "",
                    alt: "Pharmaceuticals project thumbnail for Global Pharmaceutical Industries (EIA-IND-012)"
                }
            },
            {
                id: "EIA-WTE-002",
                slug: "eia-wte-002-tagaddod-for-software-and-new-renewable-energy-services",
                client: "Tagaddod for Software and New & Renewable Energy Services",
                projectType: "EIA",
                sector: "Renewable Fuels",
                location: "Egypt",
                primaryServiceLine: "Renewable Energy, Bioenergy, and Waste-to-Energy",
                oneSentenceOutcome: "The project gained a clearer compliance basis for renewable fuels operations and expansion planning.",
                thumbnail: {
                    src: "",
                    alt: "Renewable Fuels project thumbnail for Tagaddod for Software and New & Renewable Energy Services (EIA-WTE-002)"
                }
            },
            {
                id: "EIA-EDU-001",
                slug: "eia-edu-001-school-complex-ibs-bls",
                client: "School Complex (IBS/BLS)",
                projectType: "EIA",
                sector: "Education",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Education project thumbnail for School Complex (IBS/BLS) (EIA-EDU-001)"
                }
            },
            {
                id: "EIA-IND-013",
                slug: "eia-ind-013-gama-pack-for-carton",
                client: "Gama Pack for Carton",
                projectType: "EIA",
                sector: "Packaging / Carton",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Packaging / Carton project thumbnail for Gama Pack for Carton (EIA-IND-013)"
                }
            },
            {
                id: "EIA-IND-014",
                slug: "eia-ind-014-egyptian-swiss-company-for-pulp",
                client: "Egyptian Swiss Company for Pulp",
                projectType: "EIA",
                sector: "Pulp / Paper",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                thumbnail: {
                    src: "",
                    alt: "Pulp / Paper project thumbnail for Egyptian Swiss Company for Pulp (EIA-IND-014)"
                }
            },
            {
                id: "EIA-CATC-002",
                slug: "eia-catc-002-al-rajhi-international-for-agricultural-investment-s-a-e",
                client: "Al-Rajhi International for Agricultural Investment (S.A.E.)",
                projectType: "Category (C) EIA",
                sector: "Agriculture Investment",
                location: "Toshka, Aswan Governorate, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                oneSentenceOutcome: "The study provided a structured basis for managing environmental risks in large-scale cultivation\nprograms.",
                thumbnail: {
                    src: "",
                    alt: "Agriculture Investment project thumbnail for Al-Rajhi International for Agricultural Investment (S.A.E.) (EIA-CATC-002)"
                }
            },
            {
                id: "EIA-CATC-003",
                slug: "eia-catc-003-egyptian-environmental-services-project-hazardous-transport",
                client: "Egyptian Environmental Services Project (hazardous transport)",
                projectType: "Category (C) EIA",
                sector: "Hazardous Waste Logistics",
                location: "Egypt (all governorates)",
                primaryServiceLine: "Waste Management and Hazardous Streams",
                oneSentenceOutcome: "The study improved decision clarity and supported a smoother approvals pathway.",
                thumbnail: {
                    src: "",
                    alt: "Hazardous Waste Logistics project thumbnail for Egyptian Environmental Services Project (hazardous transport) (EIA-CATC-003)"
                }
            },
            {
                id: "REG-ENV-001",
                slug: "reg-env-001-royal-pack",
                client: "Royal Pack",
                projectType: "Compliance Registers",
                sector: "Food Manufacturing",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Food Manufacturing project thumbnail for Royal Pack (REG-ENV-001)"
                }
            },
            {
                id: "REG-ENV-002",
                slug: "reg-env-002-al-nour",
                client: "Al-Nour",
                projectType: "Compliance Registers",
                sector: "Dairy Products",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Dairy Products project thumbnail for Al-Nour (REG-ENV-002)"
                }
            },
            {
                id: "REG-ENV-003",
                slug: "reg-env-003-best-cheese",
                client: "Best Cheese",
                projectType: "Compliance Registers",
                sector: "Dairy Products",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Dairy Products project thumbnail for Best Cheese (REG-ENV-003)"
                }
            },
            {
                id: "REG-ENV-004",
                slug: "reg-env-004-italian-company-for-extracting-and-refining-oils",
                client: "Italian Company for Extracting and Refining Oils",
                projectType: "Compliance Registers",
                sector: "Oils / Food Industry",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Oils / Food Industry project thumbnail for Italian Company for Extracting and Refining Oils (REG-ENV-004)"
                }
            },
            {
                id: "REG-ENV-005",
                slug: "reg-env-005-egyptian-international-company-for-textile-trading-and-manufacturing",
                client: "Egyptian International Company for Textile Trading and Manufacturing",
                projectType: "Compliance Registers",
                sector: "Textiles",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Textiles project thumbnail for Egyptian International Company for Textile Trading and Manufacturing (REG-ENV-005)"
                }
            },
            {
                id: "REG-ENV-006",
                slug: "reg-env-006-interstate-paper-industries",
                client: "Interstate Paper Industries",
                projectType: "Compliance Registers",
                sector: "Pulp / Paper",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Pulp / Paper project thumbnail for Interstate Paper Industries (REG-ENV-006)"
                }
            },
            {
                id: "REG-ENV-007",
                slug: "reg-env-007-al-rajhi-international-for-agricultural-investment-s-a-e",
                client: "Al-Rajhi International for Agricultural Investment (S.A.E.)",
                projectType: "Compliance Registers",
                sector: "Agriculture Investment",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Agriculture Investment project thumbnail for Al-Rajhi International for Agricultural Investment (S.A.E.) (REG-ENV-007)"
                }
            },
            {
                id: "REG-ENV-008",
                slug: "reg-env-008-alamia-star",
                client: "Alamia Star",
                projectType: "Compliance Registers",
                sector: "Textile Industry and Dyeing",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Textile Industry and Dyeing project thumbnail for Alamia Star (REG-ENV-008)"
                }
            },
            {
                id: "REG-ENV-009",
                slug: "reg-env-009-al-fanni-factory-for-metals",
                client: "Al-Fanni Factory for Metals",
                projectType: "Compliance Registers",
                sector: "Metals",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Metals project thumbnail for Al-Fanni Factory for Metals (REG-ENV-009)"
                }
            },
            {
                id: "REG-ENV-010",
                slug: "reg-env-010-group-sp-egypt-for-home-appliances",
                client: "Group SP Egypt for Home Appliances",
                projectType: "Compliance Registers",
                sector: "Home Appliances",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                oneSentenceOutcome: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                thumbnail: {
                    src: "",
                    alt: "Home Appliances project thumbnail for Group SP Egypt for Home Appliances (REG-ENV-010)"
                }
            }
        ],
        caseStudies: [
            {
                id: "EIA-ROB-01",
                title: "Mohamed Harby and Partners",
                client: "Mohamed Harby and Partners",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Mohamed Harby and Partners. This engagement focused on integrate new wastewater\ntreatment capacity into compliance documentation, including sludge handling and reuse/discharge\ncontrols.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 42"
            },
            {
                id: "EIA-ROB-02",
                title: "Tannery \"Geneh\" (Mostafa Hussein Abdel Khaleq)",
                client: "Tannery \"Geneh\" (Mostafa Hussein Abdel Khaleq)",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Tannery \"Geneh\" (Mostafa Hussein Abdel Khaleq). This engagement focused on integrate\nnew wastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 43"
            },
            {
                id: "EIA-ROB-03",
                title: "Mahmoud Mohamed Sarag Tannery",
                client: "Mahmoud Mohamed Sarag Tannery",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Mahmoud Mohamed Sarag Tannery. This engagement focused on integrate new\nwastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-ROB-04",
                title: "El-Montazah Tannery (Hamdy Mohamed Harb)",
                client: "El-Montazah Tannery (Hamdy Mohamed Harb)",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - El-Montazah Tannery (Hamdy Mohamed Harb). This engagement focused on integrate new\nwastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 45"
            },
            {
                id: "EIA-ROB-05",
                title: "German Industry and Trade Company \"El-Gebbas\"",
                client: "German Industry and Trade Company \"El-Gebbas\"",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - German Industry and Trade Company \"El-Gebbas\". This engagement focused on integrate\nnew wastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-ROB-06",
                title: "Ahmed Harby and Partners",
                client: "Ahmed Harby and Partners",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Ahmed Harby and Partners. This engagement focused on integrate new wastewater\ntreatment capacity into compliance documentation, including sludge handling and reuse/discharge\ncontrols.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 47"
            },
            {
                id: "EIA-ROB-07",
                title: "Ammar Bilal Ammar Tannery",
                client: "Ammar Bilal Ammar Tannery",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Ammar Bilal Ammar Tannery. This engagement focused on integrate new wastewater\ntreatment capacity into compliance documentation, including sludge handling and reuse/discharge\ncontrols.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-ROB-08",
                title: "Serag Ammar Tannery",
                client: "Serag Ammar Tannery",
                projectType: "EIA",
                sector: "Leather / Tanneries",
                location: "Robeiki (Leather City), Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Serag Ammar Tannery. This engagement focused on integrate new wastewater treatment\ncapacity into compliance documentation, including sludge handling and reuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 49"
            },
            {
                id: "EIA-IND-001",
                title: "National Paints Factories Company",
                client: "National Paints Factories Company",
                projectType: "EIA",
                sector: "Paints / Chemicals",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Industrial wastewater treatment unit addition. This engagement focused on integrate new\nwastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-IND-002",
                title: "Smart Home for Electrical Industries",
                client: "Smart Home for Electrical Industries",
                projectType: "EIA",
                sector: "Electrical Manufacturing",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Smart Home for Electrical Industries. This engagement focused on document impacts and\ndefine mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 51"
            },
            {
                id: "EIA-IND-003",
                title: "Aura Aerosols",
                client: "Aura Aerosols",
                projectType: "EIA",
                sector: "Aerosols / FMCG",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Aura Aerosols (air freshener production). This engagement focused on document impacts\nand define mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-IND-004",
                title: "Egyptian International Company for Textile and Garment",
                client: "Egyptian International Company for Textile and Garment Trading & Manufacturing",
                projectType: "EIA",
                sector: "Textiles / Garments",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Industrial wastewater treatment unit addition. This engagement focused on integrate new\nwastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 53"
            },
            {
                id: "EIA-IND-005",
                title: "Italian Company for Extracting and Refining Oils",
                client: "Italian Company for Extracting and Refining Oils",
                projectType: "EIA",
                sector: "Oils / Food Industry",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Extracting and refining oils facility. This engagement focused on document impacts and\ndefine mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-WAT-001",
                title: "Egyptian Propylene and Polypropylene Production",
                client: "Egyptian Propylene and Polypropylene Production Company",
                projectType: "EIA",
                sector: "Petrochemical / Water",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Marine outfall for high-salinity water from desalination plant. This engagement focused on\nassess potential impacts of high-salinity discharge and define monitoring and operational controls for\nmarine protection.",
                scopeOfWork: [
                    "Documented baseline conditions and identified sensitive receptors.",
                    "Assessed dispersion/dilution assumptions and discharge controls.",
                    "Defined monitoring triggers and contingency actions."
                ],
                keyDeliverables: [
                    "Authority-ready EIA report with a practical mitigation package.",
                    "Monitoring program (parameters, frequency, responsibilities).",
                    "Operational controls for normal and abnormal conditions."
                ],
                valueDelivered: "The project team gained a defensible plan for managing salinity-related risks and proving compliance\nover time.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 55"
            },
            {
                id: "EIA-IND-006",
                title: "Raad Glass Factory",
                client: "Raad Glass Factory",
                projectType: "EIA",
                sector: "Glass Manufacturing",
                location: "Sadat City, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Raad Glass Factory project. This engagement focused on document impacts and define\nmitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-IND-007",
                title: "Arab Company for Resins and Chemical Industries",
                client: "Arab Company for Resins and Chemical Industries",
                projectType: "EIA",
                sector: "Resins / Chemicals",
                location: "New Borg El-Arab, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Arab Company for Resins and Chemical Industries. This engagement focused on document\nimpacts and define mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 57"
            },
            {
                id: "EIA-IND-008",
                title: "E-Star Egypt for Industry (S.A.E.)",
                client: "E-Star Egypt for Industry (S.A.E.)",
                projectType: "EIA",
                sector: "Steel / Industry",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - E-Star Egypt for Industry (S.A.E.). This engagement focused on document impacts and\ndefine mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-IND-009",
                title: "Al-Andalus Iron Industries",
                client: "Al-Andalus Iron Industries",
                projectType: "EIA",
                sector: "Metals / Foundry",
                location: "Beni Suef, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Al-Andalus Iron Industries (cast iron foundry). This engagement focused on document\nimpacts and define mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 59"
            },
            {
                id: "EIA-LIM-001",
                title: "Egyptian Propylene and Polypropylene Production",
                client: "Egyptian Propylene and Polypropylene Production Company",
                projectType: "Limited EIA",
                sector: "Petrochemical",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "Limited EIA - New boiler addition. This engagement focused on document impacts and define mitigation\nand monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-LIM-002",
                title: "Group SP Egypt for Home Appliances",
                client: "Group SP Egypt for Home Appliances",
                projectType: "Limited EIA",
                sector: "Home Appliances",
                location: "Borg El-Arab, Alexandria, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "Limited EIA - Group SP Egypt for Home Appliances. This engagement focused on document impacts\nand define mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 61"
            },
            {
                id: "EIA-LIM-003",
                title: "Al-Anwar for Extraction of Vegetable Oils",
                client: "Al-Anwar for Extraction of Vegetable Oils",
                projectType: "Limited EIA",
                sector: "Oils / Food Industry",
                location: "Beheira, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "Limited EIA - Al-Anwar for extraction of vegetable oils. This engagement focused on document impacts\nand define mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-INF-001",
                title: "Road Project (Abu Omar Road)",
                client: "Road Project (Abu Omar Road)",
                projectType: "EIA",
                sector: "Infrastructure / Roads",
                location: "Kafr El-Sheikh Governorate, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Abu Omar Road upgrade, development and resurfacing. This engagement focused on\ncontrol construction impacts (dust, noise, detours, safety) and document enforceable mitigation along\nthe corridor.",
                scopeOfWork: [
                    "Defined construction-phase impacts and sensitive receptors along the route.",
                    "Prepared enforceable mitigation measures for contractors (dust, noise windows, traffic management).",
                    "Designed a monitoring checklist program and reporting cadence."
                ],
                keyDeliverables: [
                    "Impact assessment separating construction vs operation.",
                    "Mitigation plan and contractor control requirements.",
                    "Monitoring checklists and reporting templates."
                ],
                valueDelivered: "The study supported a clearer approvals pathway and translated mitigation into site-enforceable\nactions.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 63"
            },
            {
                id: "EIA-IND-010",
                title: "Modern Chemical Industries (MCI)",
                client: "Modern Chemical Industries (MCI)",
                projectType: "EIA",
                sector: "Chemicals",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Modern Chemical Industries (printing inks, paints, adhesives). This engagement focused on\ndocument impacts and define mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-IND-011",
                title: "Egyptian International Textiles Company",
                client: "Egyptian International Textiles Company",
                projectType: "EIA",
                sector: "Textiles",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Expansion (electrical capacity and production capacity). This engagement focused on\ndocument impacts and define mitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 65"
            },
            {
                id: "EIA-WTE-001",
                title: "Tagaddod for Software and New & Renewable Energy",
                client: "Tagaddod for Software and New & Renewable Energy Services",
                projectType: "EIA",
                sector: "Renewable Fuels",
                location: "Egypt",
                primaryServiceLine: "Renewable Energy, Bioenergy, and Waste-to-Energy",
                overview: "EIA Study - Biodiesel from used cooking oil. This engagement focused on support renewable fuels\ndevelopment with clear documentation of feedstock handling, safety controls, emissions, and residual\nrouting.",
                scopeOfWork: [
                    "Documented feedstock inputs and storage controls.",
                    "Mapped process steps and potential emissions and wastes.",
                    "Defined mitigation and monitoring tailored to flammables and process safety."
                ],
                keyDeliverables: [
                    "EIA report with a process-focused impact evaluation.",
                    "Safety and storage controls integrated into the mitigation plan.",
                    "Monitoring KPIs and reporting routine definitions."
                ],
                valueDelivered: "The project gained a clearer compliance basis for renewable fuels operations and expansion planning.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-INF-002",
                title: "Road Project (Kafr Askar link)",
                client: "Road Project (Kafr Askar link)",
                projectType: "EIA",
                sector: "Infrastructure / Roads",
                location: "Kafr El-Sheikh Governorate, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Road linking Kafr El-Sheikh/Baltim branch road via Kafr Askar. This engagement focused\non control construction impacts (dust, noise, detours, safety) and document enforceable mitigation\nalong the corridor.",
                scopeOfWork: [
                    "Defined construction-phase impacts and sensitive receptors along the route.",
                    "Prepared enforceable mitigation measures for contractors (dust, noise windows, traffic management).",
                    "Designed a monitoring checklist program and reporting cadence."
                ],
                keyDeliverables: [
                    "Impact assessment separating construction vs operation.",
                    "Mitigation plan and contractor control requirements.",
                    "Monitoring checklists and reporting templates."
                ],
                valueDelivered: "The study supported a clearer approvals pathway and translated mitigation into site-enforceable\nactions.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 67"
            },
            {
                id: "EIA-CATC-001",
                title: "Egyptian Environmental Services Company (expansion)",
                client: "Egyptian Environmental Services Company (expansion)",
                projectType: "Category (C) EIA",
                sector: "Waste Services / Nationwide",
                location: "Egypt (nationwide)",
                primaryServiceLine: "Waste Management and Hazardous Streams",
                overview: "Category (C) EIA - Nationwide scope expansion for collection and transport of\nhazardous/non-hazardous wastes. This engagement focused on document impacts and define\nmitigation and monitoring for approval and stable operations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-IND-012",
                title: "Global Pharmaceutical Industries",
                client: "Global Pharmaceutical Industries",
                projectType: "EIA",
                sector: "Pharmaceuticals",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Global Pharmaceutical Industries expansions. This engagement focused on assess\nexpansion impacts without disrupting operations, including waste streams, utilities, and controlled-area\nmodifications.",
                scopeOfWork: [
                    "Documented expansion elements (equipment additions, room modifications, packaging areas).",
                    "Assessed incremental impacts and ensured permit consistency.",
                    "Updated mitigation and monitoring measures accordingly."
                ],
                keyDeliverables: [
                    "EIA report focused on incremental change and compliance.",
                    "Updated waste management and monitoring requirements.",
                    "Implementation notes for construction and commissioning controls."
                ],
                valueDelivered: "The expansion assessment supported review readiness and controlled transition into upgraded\noperations.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 69"
            },
            {
                id: "EIA-WTE-002",
                title: "Tagaddod for Software and New & Renewable Energy",
                client: "Tagaddod for Software and New & Renewable Energy Services",
                projectType: "EIA",
                sector: "Renewable Fuels",
                location: "Egypt",
                primaryServiceLine: "Renewable Energy, Bioenergy, and Waste-to-Energy",
                overview: "EIA Study - Ethanol production line expansion. This engagement focused on support renewable fuels\ndevelopment with clear documentation of feedstock handling, safety controls, emissions, and residual\nrouting.",
                scopeOfWork: [
                    "Documented feedstock inputs and storage controls.",
                    "Mapped process steps and potential emissions and wastes.",
                    "Defined mitigation and monitoring tailored to flammables and process safety."
                ],
                keyDeliverables: [
                    "EIA report with a process-focused impact evaluation.",
                    "Safety and storage controls integrated into the mitigation plan.",
                    "Monitoring KPIs and reporting routine definitions."
                ],
                valueDelivered: "The project gained a clearer compliance basis for renewable fuels operations and expansion planning.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-EDU-001",
                title: "School Complex (IBS/BLS)",
                client: "School Complex (IBS/BLS)",
                projectType: "EIA",
                sector: "Education",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - School complex (International British School and British Languages School). This\nengagement focused on document impacts and define mitigation and monitoring for approval and stable\noperations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 71"
            },
            {
                id: "EIA-IND-013",
                title: "Gama Pack for Carton",
                client: "Gama Pack for Carton",
                projectType: "EIA",
                sector: "Packaging / Carton",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Industrial wastewater treatment plant for reuse. This engagement focused on integrate new\nwastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-IND-014",
                title: "Egyptian Swiss Company for Pulp",
                client: "Egyptian Swiss Company for Pulp",
                projectType: "EIA",
                sector: "Pulp / Paper",
                location: "Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "EIA Study - Industrial wastewater treatment plant for reuse. This engagement focused on integrate new\nwastewater treatment capacity into compliance documentation, including sludge handling and\nreuse/discharge controls.",
                scopeOfWork: [
                    "Mapped influent sources, expected flows, and treatment stages at a concept level.",
                    "Assessed residuals (sludge) handling, storage, and routing requirements.",
                    "Defined monitoring KPIs for treatment performance and compliance."
                ],
                keyDeliverables: [
                    "EIA documenting the WWTP addition, its impacts, and mitigation.",
                    "Sludge management and routing description (audit-ready).",
                    "Monitoring plan aligned with operational reality."
                ],
                valueDelivered: "The study clarified requirements for safe operation and supported smoother review of the WWTP\naddition.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 73"
            },
            {
                id: "EIA-CATC-002",
                title: "Al-Rajhi International for Agricultural Investment (S.A.E.)",
                client: "Al-Rajhi International for Agricultural Investment (S.A.E.)",
                projectType: "Category (C) EIA",
                sector: "Agriculture Investment",
                location: "Toshka, Aswan Governorate, Egypt",
                primaryServiceLine: "Environmental and Social Assessments",
                overview: "Category (C) EIA - Agricultural investment cultivation project (42,000 feddans). This engagement\nfocused on assess water balance, soil impacts, and ecosystem risks across a large cultivation footprint\nand define measurable controls.",
                scopeOfWork: [
                    "Reviewed irrigation and water balance drivers and potential salinization risks.",
                    "Defined erosion/runoff controls and soil monitoring approach.",
                    "Outlined coordination needs for phased development."
                ],
                keyDeliverables: [
                    "Category (C) documentation aligned to large-scope requirements.",
                    "Mitigation and monitoring KPIs for water and soil protection.",
                    "Implementation guidance for phased roll-out."
                ],
                valueDelivered: "The study provided a structured basis for managing environmental risks in large-scale cultivation\nprograms.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "EIA-CATC-003",
                title: "Egyptian Environmental Services Project (hazardous",
                client: "Egyptian Environmental Services Project (hazardous transport)",
                projectType: "Category (C) EIA",
                sector: "Hazardous Waste Logistics",
                location: "Egypt (all governorates)",
                primaryServiceLine: "Waste Management and Hazardous Streams",
                overview: "Category (C) EIA - Nationwide hazardous waste transport expansion (all governorates). This\nengagement focused on document impacts and define mitigation and monitoring for approval and stable\noperations.",
                scopeOfWork: [
                    "Clarified project description, process flow, and key impact pathways.",
                    "Assessed impacts and defined mitigation measures and monitoring KPIs.",
                    "Prepared authority-ready documentation and responses."
                ],
                keyDeliverables: [
                    "EIA (or limited EIA) report with EMP and monitoring plan.",
                    "Mitigation measures translated into implementable controls.",
                    "Submission-ready formatting and review response support."
                ],
                valueDelivered: "The study improved decision clarity and supported a smoother approvals pathway.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 75"
            },
            {
                id: "REG-ENV-001",
                title: "Royal Pack",
                client: "Royal Pack",
                projectType: "Compliance Registers",
                sector: "Food Manufacturing",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "REG-ENV-002",
                title: "Al-Nour",
                client: "Al-Nour",
                projectType: "Compliance Registers",
                sector: "Dairy Products",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 77"
            },
            {
                id: "REG-ENV-003",
                title: "Best Cheese",
                client: "Best Cheese",
                projectType: "Compliance Registers",
                sector: "Dairy Products",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "REG-ENV-004",
                title: "Italian Company for Extracting and Refining Oils",
                client: "Italian Company for Extracting and Refining Oils",
                projectType: "Compliance Registers",
                sector: "Oils / Food Industry",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 79"
            },
            {
                id: "REG-ENV-005",
                title: "Egyptian International Company for Textile Trading and",
                client: "Egyptian International Company for Textile Trading and Manufacturing",
                projectType: "Compliance Registers",
                sector: "Textiles",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "REG-ENV-006",
                title: "Interstate Paper Industries",
                client: "Interstate Paper Industries",
                projectType: "Compliance Registers",
                sector: "Pulp / Paper",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 81"
            },
            {
                id: "REG-ENV-007",
                title: "Al-Rajhi International for Agricultural Investment (S.A.E.)",
                client: "Al-Rajhi International for Agricultural Investment (S.A.E.)",
                projectType: "Compliance Registers",
                sector: "Agriculture Investment",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "REG-ENV-008",
                title: "Alamia Star",
                client: "Alamia Star",
                projectType: "Compliance Registers",
                sector: "Textile Industry and Dyeing",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste).\n\nMega Green\nWebsite Content Pack\nEstablished 2020 • Environmental Consulting • Renewable Energy • Waste Management\nPage 83"
            },
            {
                id: "REG-ENV-009",
                title: "Al-Fanni Factory for Metals",
                client: "Al-Fanni Factory for Metals",
                projectType: "Compliance Registers",
                sector: "Metals",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            },
            {
                id: "REG-ENV-010",
                title: "Group SP Egypt for Home Appliances",
                client: "Group SP Egypt for Home Appliances",
                projectType: "Compliance Registers",
                sector: "Home Appliances",
                location: "Egypt",
                primaryServiceLine: "Environmental Compliance and Registers",
                overview: "Environmental register and hazardous materials/waste register. This engagement focused on improve\naudit readiness and maintain day-to-day control over hazardous materials and waste streams.",
                scopeOfWork: [
                    "Collected process and chemicals inventory (including SDS where available).",
                    "Built an environmental register and a hazardous materials and waste register tied to storage locations.",
                    "Defined routine inspections, labeling rules, and routing documentation requirements."
                ],
                keyDeliverables: [
                    "Registers in an operational format (owner, location, controls, frequency).",
                    "Inspection checklists and corrective action tracking template.",
                    "Recommendations for segregation, signage, and contractor documentation."
                ],
                valueDelivered: "The facility gained a clearer compliance baseline and a repeatable system to prepare for inspections\nand internal audits.",
                imagerySuggestion: "If client photos are unavailable: use sector imagery + a small document/map overlay. Include one simple diagram:\nprocess flow (industrial) or corridor map (roads) or system map (nationwide waste)."
            }
        ],
    },
    insights: {
        intro: "Short, practical articles that help teams navigate approvals, inspections, and project decisions.",
        articles: [
            {
                slug: "environmental-impact-assessment-eia-what-it-is-and-why-it-accelerates-approvals",
                title: "Environmental Impact Assessment (EIA): What It Is and Why It Accelerates Approvals",
                deck: "A practical, non-academic guide to how EIAs work, what reviewers expect, and how to avoid the most common delays.",
                body: "An Environmental Impact Assessment (EIA) is a structured process used to predict and manage a project's effects before construction begins. In practice, it is your roadmap for reducing approval risk: it clarifies what impacts matter, how they will be mitigated, and how compliance will be proven during operation. The fastest EIAs are not the shortest — they are the clearest. Reviewers look for a coherent story: the project description must match the layout and the process flow, the sources of emissions and wastes must be quantified, and mitigation must be specific (not generic). A strong EIA separates construction impacts from operational impacts. Construction is about dust, noise, traffic, and waste from site works. Operations are about steady-state emissions, wastewater loads, hazardous storage, and monitoring routines. When these phases are mixed together, approvals slow down. At Mega Green, we emphasize scoping early. That means agreeing on what is required, collecting the right data once, and presenting it in an authority-ready format. The result is a study that supports decisions, not a document that creates more questions.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/environmental-impact-assessment-eia-what-it-is-and-why-it-accelerates-approvals.jpg"
            },
            {
                slug: "limited-eia-vs-full-eia-choosing-the-proportional-path",
                title: "Limited EIA vs Full EIA: Choosing the Proportional Path",
                deck: "How to decide whether a change (equipment, boiler, expansion) needs a limited EIA or a full assessment — and how to document it.",
                body: "Many projects are not new facilities — they are upgrades: a new boiler, a production room modification, an added packing area, or a capacity increase. The right approach is a proportional study that focuses only on what changes. A limited EIA typically concentrates on the delta: new emission points, new loads on wastewater, changes in waste streams, and any new risks to neighbors or sensitive receptors. It also checks whether existing permits and monitoring programs remain adequate. The key is consistency. The equipment datasheet, emission controls, operating hours, and monitoring plan must align. If they do, reviewers can evaluate quickly. If they conflict, the review turns into a back-and-forth request for clarifications. Mega Green structures limited EIAs with a simple logic: baseline — change — impact — mitigation — monitoring. This keeps decisions fast and defensible.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/limited-eia-vs-full-eia-choosing-the-proportional-path.jpg"
            },
            {
                slug: "category-c-eias-for-nationwide-operations-what-makes-them-different",
                title: "Category (C) EIAs for Nationwide Operations: What Makes Them Different",
                deck: "When a project spans governorates and includes multiple waste classes, the EIA must prove control, traceability, and governance.",
                body: "Large-scope projects — such as nationwide collection and transport of hazardous and non-hazardous waste — require more than a site-based assessment. They require a system-based assessment. A Category (C) EIA must show how the operation is governed: how waste is classified, how routes and transfer points are controlled, how contractors are qualified, and how chain-of-custody is documented. Authorities look for proof that the system prevents leakage, mixing of incompatible streams, and unsafe storage. The strongest studies use clear operational rules: segregation matrices, packaging standards, labeling and signage, emergency response, and a traceability workflow that can be audited. This is where policy meets day-to-day operations. Mega Green supports these projects by translating complex operations into clear documentation, with KPIs that make compliance measurable and manageable.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/category-c-eias-for-nationwide-operations-what-makes-them-different.jpg"
            },
            {
                slug: "waste-management-concepts-turning-material-flow-into-action",
                title: "Waste Management Concepts: Turning Material Flow Into Action",
                deck: "Waste concepts fail when they stay at the level of slogans. Here is what makes a concept implementable.",
                body: "The goal of a waste management concept is not to describe waste — it is to change outcomes: reduce generation, increase reuse and recycling, and ensure safe treatment and disposal. An implementable concept starts with a material flow analysis (MFA). What comes in, what is produced, what is recyclable, what is hazardous, and what must be treated? Once the flows are clear, the concept defines infrastructure: sorting areas, storage times, container types, signage, and routing to approved facilities. Operational details matter: who records quantities, who signs manifests, how incidents are handled, and how subcontractors are controlled. Without these details, recovery targets are not realistic. Mega Green produces concepts that include SOPs, training modules, and KPI definitions so implementation can begin immediately after approval.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/waste-management-concepts-turning-material-flow-into-action.jpg"
            },
            {
                slug: "hazardous-materials-and-waste-registers-your-best-defense-in-an-inspection",
                title: "Hazardous Materials and Waste Registers: Your Best Defense in an Inspection",
                deck: "Registers are not paperwork — they are the control center for chemical safety and waste routing.",
                body: "Environmental registers and hazardous materials/waste registers create a single source of truth: what substances exist on-site, what hazards they carry, where they are stored, and how wastes are classified and routed. When an inspection happens, uncertainty creates risk. A register turns uncertainty into a checklist: storage compatibility, labeling, secondary containment, max storage times, PPE, and contractor documentation. A good register is operational. It includes the location, the responsible owner, the required control measures, and the routine checks. It also includes action items when gaps exist — and a timeline to close them. Mega Green builds registers with audit readiness in mind: simple structures, clear responsibilities, and document templates that teams actually use.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/hazardous-materials-and-waste-registers-your-best-defense-in-an-inspection.jpg"
            },
            {
                slug: "industrial-wastewater-treatment-additions-what-your-eia-must-cover",
                title: "Industrial Wastewater Treatment Additions: What Your EIA Must Cover",
                deck: "Adding a WWTP or reuse system changes your environmental profile. Here is how to document it clearly.",
                body: "When a facility adds an industrial wastewater treatment unit or a reuse plant, regulators expect a clear description of influent quality, treatment stages, sludge management, and final discharge or reuse. The assessment should define: expected flow rates, peak factors, chemical dosing, sludge quantities, and how residuals are stored and transported. It must also address odor, noise, and any risks to workers and neighbors. Many delays happen because sludge routing is not defined. A complete study treats sludge as a primary output: classification, storage, and final disposal must be specified. Mega Green integrates WWTP design assumptions with the operational plan, so the EIA supports real implementation — not just approval.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/industrial-wastewater-treatment-additions-what-your-eia-must-cover.jpg"
            },
            {
                slug: "marine-outfalls-and-high-salinity-discharge-a-practical-mitigation-checklist",
                title: "Marine Outfalls and High-Salinity Discharge: A Practical Mitigation Checklist",
                deck: "Outfall studies are about modeling plus monitoring — but the winning studies are the ones that connect both.",
                body: "Marine outfalls for desalination brine or industrial discharges require a different lens: dilution and dispersion, receiving water quality, and ecological sensitivity. A practical assessment includes a baseline survey, a dispersion model, and a monitoring plan that can validate assumptions during operation. It also defines operational controls: discharge limits, maintenance, emergency shutdown procedures, and reporting triggers. Mitigation is rarely a single measure. It is a package: diffuser design, discharge scheduling, monitoring frequency, and contingency actions if thresholds are approached. Mega Green frames outfall assessments to be defensible: assumptions are stated, uncertainties are managed, and monitoring closes the loop.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/marine-outfalls-and-high-salinity-discharge-a-practical-mitigation-checklist.jpg"
            },
            {
                slug: "road-project-eias-managing-dust-noise-and-community-safety",
                title: "Road Project EIAs: Managing Dust, Noise, and Community Safety",
                deck: "Infrastructure EIAs succeed when the construction plan is treated as the core environmental risk — and made enforceable.",
                body: "For roads, most environmental impacts occur during construction: dust from earthworks, noise from equipment, traffic detours, and community safety risks near schools and residential areas. The best EIAs translate mitigation into contractor rules: watering schedules, covered trucks, speed limits, no-idling policies, noise time windows, and clear signage and barriers. Monitoring is then simple: daily checklists and weekly reporting. Material sourcing and waste disposal can become hidden risks. A strong study defines approved sources, spoil management rules, and housekeeping standards along the corridor. Mega Green builds road EIAs around construction control, because that is what actually protects communities and keeps projects on schedule.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/road-project-eias-managing-dust-noise-and-community-safety.jpg"
            },
            {
                slug: "biofuels-projects-uco-to-biodiesel-and-ethanol-line-expansions",
                title: "Biofuels Projects: UCO-to-Biodiesel and Ethanol Line Expansions",
                deck: "A clear view of feedstock, storage, emissions, and safety is what keeps renewable fuel projects bankable and approvable.",
                body: "Renewable fuel projects depend on stable feedstock and strong controls. For used cooking oil (UCO) biodiesel, the key topics are supply variability, storage safety, pretreatment residues, and wastewater handling. For ethanol expansions, the focus shifts to process safety, emissions control, and byproduct routing. In both cases, regulators and stakeholders want clarity: what comes in, what goes out, what wastes exist, and how monitoring will prove compliance. Safety is central: flammables storage, firefighting readiness, and emergency response must be integrated into the environmental plan. Mega Green approaches biofuel assessments with a process-first lens: map the mass balance, define controls at each step, and ensure wastes and residuals have approved pathways.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/biofuels-projects-uco-to-biodiesel-and-ethanol-line-expansions.jpg"
            },
            {
                slug: "training-and-capacity-building-the-fastest-way-to-reduce-environmental-risk",
                title: "Training and Capacity Building: The Fastest Way to Reduce Environmental Risk",
                deck: "Facilities become compliant when people know exactly what to do, and when systems make the right action easy.",
                body: "Many violations are not caused by bad intent — they are caused by unclear procedures. Training turns regulations into daily behavior: how to segregate wastes, how to store chemicals, how to label, and how to document routing. A strong program is role-based. Operators need practical steps and visual cues. Supervisors need checklists and escalation rules. EHS teams need KPI definitions, reporting formats, and audit routines. Mega Green designs training around the site: we use the actual storage rooms, the actual container types, and the actual waste streams, then validate learning with short competency checks and refreshers.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/training-and-capacity-building-the-fastest-way-to-reduce-environmental-risk.jpg"
            },
            {
                slug: "sustainability-and-social-impact-making-large-projects-acceptable-and-durable",
                title: "Sustainability and Social Impact: Making Large Projects Acceptable and Durable",
                deck: "Social and sustainability assessments protect schedules by anticipating stakeholder concerns early.",
                body: "Large projects can be technically sound and still face delays if stakeholder concerns are not anticipated. Social and sustainability assessments identify the issues that communities and authorities care about: traffic, jobs, safety, access, and long-term environmental performance. The goal is not to overpromise — it is to define practical commitments and a communication pathway: who engages, when, and how complaints are handled. When this is documented, projects become more resilient and less likely to face late-stage pushback. Mega Green supports clients with structured stakeholder mapping, engagement plans, and reporting templates that keep communication consistent and credible.",
                suggestedVisuals: [
                    "One hero image (technical, not generic).",
                    "One diagram or checklist image as a mid-article break.",
                    "One simple KPI/monitoring table screenshot (if available)."
                ],
                tags: [],
                src: "/Insights/sustainability-and-social-impact-making-large-projects-acceptable-and-durable.jpg"
            }
        ]
    },
    downloads: {
        items: [
            {
                id: "company-profile",
                title: "Company Profile",
                desc: "Overview of Mega Green, service lines, and how we work.",
                fileName: "MegaGreen_Company_Profile.pdf",
                format: "PDF"
            },
            {
                id: "capability-statement",
                title: "Capability Statement",
                desc: "A concise summary for procurement teams and project owners.",
                fileName: "MegaGreen_Capability_Statement.pdf",
                format: "PDF"
            },
            {
                id: "project-list",
                title: "Project List",
                desc: "Current portfolio index with IDs and tags.",
                fileName: "MegaGreen_Project_List.pdf",
                format: "PDF"
            }
        ]
    },
    contact: {
        pageIntro: "Get in touch with our team to discuss your environmental consulting needs.",
        form: {
            fields: [
                "name",
                "company",
                "email",
                "phone",
                "serviceNeeded",
                "message",
            ]
        },
        email: "info@megagreen-world.com",
        phone: "+201066369001",
        address: "Omar Ibn Al-Khattab St. Star Capital Building F2-A8, Nasr City, Cairo, Egypt",
        responseTimePromise: "We reply within 1–2 business days.",
    },
    legal: {
        privacy: {
            title: "Privacy Policy",
            content: "(Add your privacy policy text.)",
        },
        terms: {
            title: "Terms of Service",
            content: "(Add your terms text.)",
        },
    },
} as const;

export type MegaGreenData = typeof megaGreenData;
